import React, { useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import Basic from "../components/Landing/Components/Basic/Basic";
import Social from "../components/Landing/Components/Social/Social";
import Upselling from "../components/Landing/Components/Upselling/Upselling";
import Main from "../components/Landing/Main/Main";
import useLandingFunctions from "../hooks/useLandingFunctions";
import useWindowSize from "../hooks/useWindowResize";
import { PFXComponentType } from "../models/Interfaces";
import {
  selectComponents,
  selectComponentsEntities,
  selectComponentsOrder,
  selectLandingId,
  selectLoadData,
} from "../redux/slices/landingSlice";
import {
  selectCartIsOpen,
  selectCleverlynkGroup,
  selectCleverlynkStage,
  selectIsMobile,
  selectItemsInCart,
  selectLoadInitialData,
  selectZendesk,
} from "../redux/slices/cleverlynkSlice";
import { Box, Button, createStyles, Fade, makeStyles, Theme } from "@material-ui/core";
import useCleverlynkFunctions from "../hooks/useCleverlynkFunctions";
import useUserFormFunctions from "../hooks/useUserFormFunctions";
import Header from "../components/Header/Header";
import Fallback from "../components/Tools/Fallback";
import SocialSpeedDials from "../components/Tools/SocialSpeedDial";
import GroupSelector from "../components/CleverlynkGroup/GroupSelector";
import LandingFooter from "../components/Footer/LandingFooter";

export const BODY_FONTSIZE_BASE = 1;
export const TITLE_FONTSIZE_BASE = 1.5;
export const BUTTON_FONTSIZE_BASE = 0.7;
export const BUTTON_PADD_BASE = 1.8;
export const MARGIN_BASE = 4;
export const DESKTOP_TEXT_MULTIPLIER = 1.3;

interface ILanding {}

const Landing = (props: ILanding) => {
  const { handleModifyComponentDimensions, handleGoToCatalog } = useLandingFunctions();

  const isMobile = useSelector(selectIsMobile);

  const components = useSelector(selectComponents);
  const componentsOrder = useSelector(selectComponentsOrder);
  const componentsEntities = useSelector(selectComponentsEntities);
  const cleverlynkStage = useSelector(selectCleverlynkStage);
  const cartIsOpen = useSelector(selectCartIsOpen);
  const loadLandingDataSupervisor = useSelector(selectLoadData);
  const loadClynkDataSupervisor = useSelector(selectLoadInitialData);
  const landingId = useSelector(selectLandingId);
  const cleverlynkGroup = useSelector(selectCleverlynkGroup);
  const cartItems = useSelector(selectItemsInCart);
  const zendesk = useSelector(selectZendesk);

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      stickToBottom: {
        width: isMobile ? "90%" : "50%",
        position: "sticky",
        maxWidth: 1280,
        margin: "auto",
        left: 0,
        zIndex: cleverlynkStage >= 1 && !cartIsOpen && cartItems.length > 0 ? 1199 : -1,
        bottom: 10,
        boxSizing: "border-box",
        MozBoxSizing: "border-box",
        WebkitBoxSizing: "border-box",
        marginTop: -100,
      },
      nextbutton: {
        height: 50,
        borderRadius: 12,
        background: `linear-gradient(26deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
        color: theme.palette.primary.contrastText,
        fontWeight: "bold",
      },
    })
  );

  const classes = useStyles();

  const windowSize = useWindowSize();

  useEffect(() => {
    handleModifyComponentDimensions(windowSize.width ?? 400, windowSize.height ?? 400);
  }, [windowSize]);

  const renderComponent = useCallback(
    (component: any) => {
      switch (component.type) {
        case PFXComponentType.BASIC:
          return <Basic component={component} key={component.id} />;

        case PFXComponentType.SOCIAL:
          return <Social component={component} key={component.id} />;

        case PFXComponentType.UPSELLING:
          return <Upselling component={component} key={component.id} />;

        default:
          return null;
      }
    },
    [components.length, componentsEntities]
  );

  const { gotToUserForm } = useCleverlynkFunctions();
  const { goToCheckout } = useUserFormFunctions();

  const buttonPropsToRender = useMemo(() => {
    switch (cleverlynkStage) {
      case 1:
        return {
          onClick: () => {
            handleGoToCatalog();
          },
          text: "Volver a menú",
        };
      case 2:
        return {
          onClick: () => {
            gotToUserForm();
          },
          text: "Volver",
        };
      case 3:
        return {
          onClick: () => {
            goToCheckout();
          },
          text: "Volver al pago",
        };
      default:
        return {
          onClick: () => null,
          text: "",
        };
    }
  }, [cleverlynkStage]);

  useEffect(() => {
    if (
      (loadLandingDataSupervisor.status === "succeeded" || loadLandingDataSupervisor.status === "failed") &&
      !landingId
    ) {
      handleGoToCatalog();
    }
  }, [landingId, loadLandingDataSupervisor]);

  return loadLandingDataSupervisor.status === "loading" || loadClynkDataSupervisor.status === "loading" ? (
    <Fallback />
  ) : (
    <React.Fragment>
      <Header atLanding />
      <div style={{ maxWidth: "100vw", overflow: "hidden" }}>
        <Main />
        {componentsOrder.map((cid, index) => renderComponent(componentsEntities[cid]))}
        <LandingFooter />
      </div>
      {cleverlynkGroup && <GroupSelector />}
      {zendesk && <SocialSpeedDials />}
      <Box p={2} className={`${classes.stickToBottom}`}>
        <Fade in={cleverlynkStage >= 1 && !cartIsOpen && cartItems.length > 0} timeout={800}>
          <Button
            color="primary"
            fullWidth
            className={classes.nextbutton}
            size="large"
            onClick={() => buttonPropsToRender.onClick()}
            variant="contained"
            id="next">
            {buttonPropsToRender.text}
          </Button>
        </Fade>
      </Box>
    </React.Fragment>
  );
};
export default Landing;
